import React, { useState } from "react";
import { useQuery } from "react-query";
import { Typography, CircularProgress, Box, Stack, Theme } from "@mui/joy";
import { useStytchMember } from "@stytch/react/b2b";
import { Button } from "@mui/joy";
import { WorkflowApi } from "../../api/workflow";
import { PrimeQueryKeys } from "../../shared/constants";
import { downloadProblemsAsCsv } from "../../util/dataUtil";
import RfiModal from "../RfiModal";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Panel, PanelGroup } from "react-resizable-panels";
import { StyledPanelResizeHandle } from "../StyledPanelResizeHandle";
import FileViewer, { FileViewerArgs } from "../fileviewer/FileViewer";
import { ApiConstructabilityIssue } from "../../shared/interfaces";
import { setPageTitle } from "../../util/elementUtil";
import { ConflictLikelihoodChip } from "./ConflictLikelihoodChip";
import IssueCard from "./IssueCard";
import { comparatorIssueNumber } from "../../util/objectUtil";

// Component-specific styles
const typographyStyles = {
    errorHeading: {
        color: "danger" as const,
        level: "h4" as const,
        mb: 2,
    },
    noProblemsFound: {
        level: "body-lg" as const,
        sx: {
            textAlign: "center",
            mt: 4,
        },
    },
};

// Layout styles
const styles = {
    topContainer: {
        flex: "1 1 auto",
        position: "relative",
        overflowY: "auto",
        display: "flex",
    },
    rightColumn: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        paddingTop: 1,
        height: "100%",
    },
    pageTitleContainer: (theme: Theme) => ({
        px: 3,
        py: 2,
        boxShadow: theme.vars.shadow.md,
        zIndex: 500,
    }),
    pageTitle: {
        fontFamily: "Overpass",
        display: "run-in",
    },
    issueList: {
        flex: "1 1 auto",
        position: "relative",
        overflowY: "auto",
        maxHeight: "100%",
        p: 2,
        display: "flex",
    },

    // Feedback states
    errorContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        flexDirection: "column",
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minHeight: "200px",
    },
};

const FileViewerMemo = React.memo(FileViewer);

export default function ConstructabilityReviewWorkflow() {
    setPageTitle("Constructability Review");

    const { member } = useStytchMember();
    const [rfiModalOpen, setRfiModalOpen] = useState(false);
    const [selectedProblem, setSelectedProblem] = useState<ApiConstructabilityIssue | null>(null);

    // Use React Query mutations to manage and cache the current file viewer state
    const [currentFileViewerFile, setCurrentFileViewerFile] = useState<FileViewerArgs | null>(null);

    // Use React Query to manage and cache workflow problems
    const {
        data: problems,
        isLoading,
        error,
    } = useQuery<ApiConstructabilityIssue[], Error>(PrimeQueryKeys.workflowProblems, () =>
        WorkflowApi.getConstructabilityIssues(),
    );



    if (error) {
        return (
            <Box sx={styles.errorContainer}>
                <Typography {...typographyStyles.errorHeading}>Error Loading Workflows</Typography>
                <Typography>{(error as Error).message}</Typography>
            </Box>
        );
    }

    const issues = (problems || []).sort(comparatorIssueNumber);
    const activeIssues = issues.filter((issue) => (issue.status || "Open") !== "Not an issue");
    const dismissedIssues = issues.filter((issue) => (issue.status || "Open") === "Not an issue");



    const formatUpdatedInfo = (issue: ApiConstructabilityIssue) => {
        if (!issue.statusChangedByUser || !issue.statusChangedAt) {
            return null;
        }
        const date = new Date(issue.statusChangedAt);
        const formattedDate =
            date.toLocaleDateString() +
            " " +
            date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

        // Check if the current user is the one who made the change
        const isCurrentUser = member?.member_id === issue.statusChangedByUser.id;
        const userDisplay = isCurrentUser ? "you" : issue.statusChangedByUser.name;

        return `Status updated by ${userDisplay} at ${formattedDate}`;
    };

    const numOfSeverity = (sev: string) =>
        issues.reduce((acc, issue) => {
            acc += issue.severity === sev ? 1 : 0;
            return acc;
        }, 0);

    return (
        <Stack sx={styles.topContainer}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={styles.pageTitleContainer}
            >
                <Stack direction="row" spacing={2}>
                    <Typography level="title-lg" sx={styles.pageTitle}>
                        Constructability Review
                    </Typography>
                </Stack>

                <Stack direction="row">
                    <Button
                        startDecorator={<FileDownloadOutlinedIcon />}
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={() => {
                            if (issues && issues.length > 0) {
                                downloadProblemsAsCsv(issues);
                            }
                        }}
                    >
                        Save spreadsheet
                    </Button>
                </Stack>
            </Stack>

            {isLoading ? (
                <Box sx={styles.loadingContainer}>
                    <CircularProgress />
                </Box>
            ) : (
                <PanelGroup direction="horizontal" autoSaveId="constructability-review">
                    <Panel minSize={40} id="issues" order={1}>
                        <Stack sx={styles.issueList} spacing={2}>
                            {issues.length === 0 && (
                                <Typography {...typographyStyles.noProblemsFound}>
                                    No problems found!
                                </Typography>
                            )}

                            {/* Active Issues Section */}
                            {activeIssues.length > 0 && (
                                <>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ p: 1 }}
                                    >
                                        <Stack spacing={0.5}>
                                            <Typography level="body-md" fontWeight="bold">
                                                {activeIssues.length} active conflicts
                                            </Typography>
                                            <Typography level="body-sm">
                                                Marvin analyzed the specifications and drawings to
                                                find potential conflicts. Remember Marvin is just an
                                                AI — please verify before acting.
                                            </Typography>
                                        </Stack>

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography level="body-xs">
                                                By conflict likelihood
                                            </Typography>
                                            <ConflictLikelihoodChip
                                                sev="high"
                                                label={`${numOfSeverity("high")} high`}
                                            />
                                            <ConflictLikelihoodChip
                                                sev="medium"
                                                label={`${numOfSeverity("medium")} medium`}
                                            />
                                            <ConflictLikelihoodChip
                                                sev="low"
                                                label={`${numOfSeverity("low")} low`}
                                            />
                                        </Stack>
                                    </Stack>

                                    {activeIssues.map((issue, index) => (
                                        <IssueCard
                                            key={issue.id}
                                            issue={issue}
                                            index={index}
                                            isDismissed={false}
                                            onRfiClick={(issue) => {
                                                setSelectedProblem(issue);
                                                setRfiModalOpen(true);
                                            }}
                                            formatUpdatedInfo={formatUpdatedInfo}
                                            currentFileViewerFile={currentFileViewerFile}
                                            setCurrentFileViewerFile={setCurrentFileViewerFile}
                                        />
                                    ))}
                                </>
                            )}

                            {/* Dismissed Issues Section */}
                            {dismissedIssues.length > 0 && (
                                <>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ p: 1, mt: 4 }}
                                    >
                                        <Stack spacing={0.5}>
                                            <Typography level="body-md" fontWeight="bold">
                                                {dismissedIssues.length} dismissed issue
                                                {dismissedIssues.length > 1 ? "s" : ""}
                                            </Typography>
                                            <Typography level="body-sm">
                                                Issues that have been marked as "Not an issue" are
                                                shown below for reference.
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    {dismissedIssues.map((issue, index) => (
                                        <IssueCard
                                            key={`dismissed-${index}`}
                                            issue={issue}
                                            index={index}
                                            isDismissed={true}
                                            onRfiClick={(issue) => {
                                                setSelectedProblem(issue);
                                                setRfiModalOpen(true);
                                            }}
                                            formatUpdatedInfo={formatUpdatedInfo}
                                            currentFileViewerFile={currentFileViewerFile}
                                            setCurrentFileViewerFile={setCurrentFileViewerFile}
                                        />
                                    ))}
                                </>
                            )}
                        </Stack>
                    </Panel>

                    {currentFileViewerFile && (
                        <>
                            <StyledPanelResizeHandle />

                            <Panel minSize={40} id="file" order={2}>
                                <Stack spacing={0} sx={styles.rightColumn}>
                                    <FileViewerMemo
                                        fileArgs={currentFileViewerFile}
                                        onClose={() => setCurrentFileViewerFile(null)}
                                        showTitleAndClose={true}
                                    />
                                </Stack>
                            </Panel>
                        </>
                    )}
                </PanelGroup>
            )}

            <RfiModal
                open={rfiModalOpen}
                onClose={() => {
                    setRfiModalOpen(false);
                    setSelectedProblem(null);
                }}
                node1Id={selectedProblem?.node1.id || ""}
                node2Id={selectedProblem?.node2.id || ""}
                description={selectedProblem?.description || ""}
            />
        </Stack>
    );
}
